import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import { GET_BROADCAST_SOURCE_OVERRIDES } from '@optools/services/operator-gateway/btc/queries';
import { BroadcastSourceOverride } from '@optools/services/operator-gateway/types/generated/graphql';
import { AddBroadcastSourceOverride } from '@optools/tools/btc-tools/components/add-broadcast-source-override';
import { DeleteBroadcastSourceOverride } from '@optools/tools/btc-tools/components/delete-broadcast-source-override';
import { UpdateBroadcastSourceOverride } from '@optools/tools/btc-tools/components/update-broadcast-source-override';
import { formatProgramSourceValue } from '@optools/tools/btc-tools/utils/broadcast-source-overrides';
import { authorizedRouteLoader } from '@optools/utils/auth/authorized-route-loader';
import { createFileRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';

export const Route = createFileRoute('/_auth/btc-tools/broadcast-source-overrides')({
  beforeLoad: authorizedRouteLoader('btcTools.broadcastSourceOverrides'),
  component: RouteComponent,
});

const columns: ColumnDef<BroadcastSourceOverride>[] = [
  {
    accessorKey: 'id',
    enableSorting: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
    id: 'ID',
  },
  {
    accessorKey: 'callSign',
    enableSorting: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Call Sign" />,
    id: 'Call Sign',
  },
  {
    accessorKey: 'region',
    enableSorting: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Region" />,
    id: 'Region',
  },
  {
    accessorKey: 'pregameSource',
    cell: ({ cell }) => {
      return (
        <p className="capitalize">{formatProgramSourceValue(cell.row.original.pregameSource)}</p>
      );
    },
    enableSorting: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Pre-Game Source" />,
    id: 'Pre-Game Source',
  },
  {
    accessorKey: 'gameSource',
    cell: ({ cell }) => {
      return <p className="capitalize">{formatProgramSourceValue(cell.row.original.gameSource)}</p>;
    },
    enableSorting: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Game Source" />,
    id: 'Game Source',
  },
  {
    accessorKey: 'postgameSource',
    cell: ({ cell }) => {
      return (
        <p className="capitalize">{formatProgramSourceValue(cell.row.original.postgameSource)}</p>
      );
    },
    enableSorting: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Post-Game Source" />,
    id: 'Post-Game Source',
  },
  {
    cell: (row) => {
      return (
        <div className="flex justify-end gap-2">
          <UpdateBroadcastSourceOverride broadcastSourceOverride={row.cell.row.original} />
          <DeleteBroadcastSourceOverride broadcastSourceOverride={row.cell.row.original} />
        </div>
      );
    },
    id: 'actions',
  },
];

export function RouteComponent() {
  const { data, loading } = useQuery(GET_BROADCAST_SOURCE_OVERRIDES);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ScrollPage>
      <DataTable columns={columns} data={data?.broadcastSourceOverrides ?? []}>
        <AddBroadcastSourceOverride />
      </DataTable>
    </ScrollPage>
  );
}
